import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import CodeInput, { DigitsChangeSources } from './CodeInput';
import { sendVerificationCode, verifyEmailCode } from '../../api/emailVerificationApi';
import {
    EMAIL_VERIFICATION_CODE_LENGTH,
    INVALID_CODE_MESSAGE,
    SUPPORT_HOME_URL,
    SendCodeOutcomes,
    SendCodeTriggers,
    countdownSecondsForSendOutcome,
    formatCountdown,
    isCodeEntrySendOutcome,
    isVerifiedOutcome,
} from '../../utils/emailVerificationPrompt';

export const PromptSteps = {
    PRESEND: 'presend',
    ENTRY: 'entry',
};

const TITLE_ID = 'email-verification-prompt-title';
const DESCRIPTION_ID = 'email-verification-prompt-description';
const CODE_ERROR_ID = 'email-verification-prompt-code-error';
const MILLISECONDS_PER_SECOND = 1000;
const PROMPT_MAX_WIDTH = 468;
const MOBILE_GUTTER = 16;

const emptyDigits = () => Array.from({ length: EMAIL_VERIFICATION_CODE_LENGTH }, () => '');

const settle = async (call) => {
    try {
        return { ok: true, value: await call() };
    } catch (error) {
        return { ok: false, error };
    }
};

// Centred card on desktop; on mobile the same card fills the width with 16px side margins
// (PENDING DESIGN, same as consumer web: Figma 270:1379 vs 297:3062).
const paperSx = (theme) => ({
    m: `${MOBILE_GUTTER}px`,
    width: `calc(100% - ${MOBILE_GUTTER * 2}px)`,
    maxWidth: PROMPT_MAX_WIDTH,
    maxHeight: `calc(100% - ${MOBILE_GUTTER * 2}px)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    p: { xs: '24px 16px 16px', sm: '32px 24px 24px' },
    textAlign: 'center',
    borderRadius: '4px',
    borderBottom: `4px solid ${theme.appColors.black}`,
});

const titleSx = (theme) => ({ m: 0, fontSize: 18, fontWeight: 700, color: theme.appColors.black });
const bodySx = (theme) => ({ m: 0, fontSize: 14, lineHeight: '20px', color: theme.appColors.darkText });
const emailSx = (theme) => ({ m: 0, fontSize: 14, fontWeight: 700, color: theme.appColors.black, wordBreak: 'break-all' });
const errorSx = (theme) => ({ m: 0, fontSize: 14, color: theme.appColors.red });
const linkButtonSx = (theme) => ({
    p: 0,
    minWidth: 0,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    color: theme.appColors.interactionBlue,
    '&:hover': { background: 'none', textDecoration: 'underline' },
});

const EmailVerificationPromptModal = ({ email, onDismiss, onVerified, 'data-testid': dataTestId }) => {
    const [step, setStep] = useState(PromptSteps.PRESEND);
    const [digits, setDigits] = useState(emptyDigits);
    const [sending, setSending] = useState(false);
    const [sendFailed, setSendFailed] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [codeError, setCodeError] = useState(false);
    const [resendDeadline, setResendDeadline] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    const inputRefs = useRef([]);
    const sendingRef = useRef(false);
    const verifyingRef = useRef(false);
    const mountedRef = useRef(true);

    const secondsLeft = resendDeadline === null ? 0 : Math.max(0, Math.ceil((resendDeadline - now) / MILLISECONDS_PER_SECOND));
    const countingDown = secondsLeft > 0;

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (step === PromptSteps.ENTRY) {
            inputRefs.current[0]?.focus();
        }
    }, [step]);

    useEffect(() => {
        if (!countingDown) {
            return undefined;
        }

        const timer = setInterval(() => setNow(Date.now()), MILLISECONDS_PER_SECOND);
        return () => clearInterval(timer);
    }, [countingDown]);

    const startCountdown = (seconds) => {
        if (seconds <= 0) {
            setResendDeadline(null);
            return;
        }

        const start = Date.now();
        setNow(start);
        setResendDeadline(start + (seconds * MILLISECONDS_PER_SECOND));
    };

    const sendCode = async (trigger) => {
        if (sendingRef.current) {
            return;
        }

        sendingRef.current = true;
        setSending(true);
        const result = await settle(() => sendVerificationCode(trigger));
        sendingRef.current = false;

        if (!mountedRef.current) {
            return;
        }

        setSending(false);

        const outcome = result.ok ? result.value?.outcome : undefined;

        if (outcome === SendCodeOutcomes.ALREADY_VERIFIED) {
            onVerified();
            return;
        }

        if (!isCodeEntrySendOutcome(outcome)) {
            setSendFailed(true);
            return;
        }

        setSendFailed(false);
        startCountdown(countdownSecondsForSendOutcome(outcome, result.value));

        if (step === PromptSteps.ENTRY) {
            setDigits(emptyDigits());
            setCodeError(false);
            inputRefs.current[0]?.focus();
            return;
        }

        setStep(PromptSteps.ENTRY);
    };

    const submitCode = async (code) => {
        if (verifyingRef.current || code.length !== EMAIL_VERIFICATION_CODE_LENGTH) {
            return;
        }

        verifyingRef.current = true;
        setVerifying(true);
        const result = await settle(() => verifyEmailCode(code));
        verifyingRef.current = false;

        if (!mountedRef.current) {
            return;
        }

        setVerifying(false);

        if (result.ok && isVerifiedOutcome(result.value?.outcome)) {
            onVerified();
            return;
        }

        // One message for every rejected code; never attempts remaining or lockout wording.
        // A rejected code is never retried as-is: empty every box and put the caret in the first one.
        // The message stays until the member types again (handleDigitsChange clears it).
        setDigits(emptyDigits());
        setCodeError(true);
        inputRefs.current[0]?.focus();
    };

    // Auto-submit only when the code becomes complete or a whole code is pasted or autofilled:
    // retyping one box of a rejected code must not spend an attempt on every keystroke.
    // submitCode settles its own request, so awaiting it here never throws.
    const handleDigitsChange = async (next, source) => {
        const wasComplete = digits.every((digit) => digit !== '');
        setDigits(next);
        setCodeError(false);
        const isComplete = next.every((digit) => digit !== '');

        if (isComplete && (source === DigitsChangeSources.FILL || !wasComplete)) {
            await submitCode(next.join(''));
        }
    };

    const handleCodeSubmit = async (event) => {
        event.preventDefault();
        await submitCode(digits.join(''));
    };

    // Escape and backdrop count as "close" (dismissed cookie), but wait for an in-flight request:
    // closing would drop its result, a successful verify included.
    const handleDialogClose = () => {
        if (sendingRef.current || verifyingRef.current) {
            return;
        }

        onDismiss();
    };

    const codeComplete = digits.every((digit) => digit !== '');

    const helpRow = (
        <Typography component="p" sx={bodySx} data-testid={dataTestId && `${dataTestId}.help-text`}>
            Need help?{' '}
            <Link
                href={SUPPORT_HOME_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={(theme) => ({ color: theme.appColors.interactionBlue })}
                data-testid={dataTestId && `${dataTestId}.support-link`}
            >
                Contact support
            </Link>
        </Typography>
    );

    const sendError = sendFailed && (
        <Box role="alert" sx={{ display: 'flex', flexDirection: 'column' }} data-testid={dataTestId && `${dataTestId}.send-error-wrapper`}>
            <Typography component="p" sx={errorSx} data-testid={dataTestId && `${dataTestId}.send-error-title`}>We couldn&apos;t send your code.</Typography>
            <Typography component="p" sx={errorSx} data-testid={dataTestId && `${dataTestId}.send-error-hint`}>Check your connection and try again.</Typography>
        </Box>
    );

    const renderPresend = () => {
        let sendLabel = 'Send code';

        if (sending) {
            sendLabel = 'Sending...';
        } else if (sendFailed) {
            sendLabel = 'Try again';
        }

        return (
            <>
                <Typography id={TITLE_ID} component="h2" sx={titleSx} data-testid={dataTestId && `${dataTestId}.title`}>
                    Please Verify Your Email
                </Typography>
                <Typography id={DESCRIPTION_ID} component="p" sx={bodySx} data-testid={dataTestId && `${dataTestId}.body`}>
                    Verify this email is still yours. It keeps your account secure and makes sure you get event updates, results and other important messages.
                </Typography>
                <Typography component="p" sx={emailSx} data-testid={dataTestId && `${dataTestId}.email`}>{email}</Typography>
                {sendError}
                <Button
                    autoFocus
                    variant="dark"
                    size="large"
                    sx={{ mt: '12px' }}
                    onClick={() => sendCode(SendCodeTriggers.INITIAL)}
                    disabled={sending}
                    data-testid={dataTestId && `${dataTestId}.send-code-btn`}
                >
                    {sendLabel}
                </Button>
                <Button
                    variant="text"
                    sx={linkButtonSx}
                    onClick={onDismiss}
                    disabled={sending}
                    data-testid={dataTestId && `${dataTestId}.skip-btn`}
                >
                    Skip For Now
                </Button>
                {helpRow}
            </>
        );
    };

    const renderEntry = () => (
        <>
            <Typography id={TITLE_ID} component="h2" sx={titleSx} data-testid={dataTestId && `${dataTestId}.code-title`}>
                Enter your code
            </Typography>
            <Typography id={DESCRIPTION_ID} component="p" sx={bodySx} data-testid={dataTestId && `${dataTestId}.sent-to-text`}>
                We sent a 6-digit code to
            </Typography>
            <Typography component="p" sx={emailSx} data-testid={dataTestId && `${dataTestId}.code-email`}>{email}</Typography>
            <Box
                component="form"
                onSubmit={handleCodeSubmit}
                noValidate
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '8px' }}
                data-testid={dataTestId && `${dataTestId}.code-form`}
            >
                <CodeInput
                    digits={digits}
                    onDigitsChange={handleDigitsChange}
                    inputRefs={inputRefs}
                    readOnly={verifying}
                    invalid={codeError}
                    describedBy={CODE_ERROR_ID}
                    data-testid={dataTestId}
                />
                <Box id={CODE_ERROR_ID} aria-live="polite" sx={{ minHeight: 20 }} data-testid={dataTestId && `${dataTestId}.code-error-wrapper`}>
                    {codeError && (
                        <Typography component="span" sx={errorSx} data-testid={dataTestId && `${dataTestId}.code-error`}>{INVALID_CODE_MESSAGE}</Typography>
                    )}
                </Box>
                <Button
                    type="submit"
                    variant="dark"
                    size="large"
                    fullWidth
                    disabled={!codeComplete || codeError || verifying}
                    aria-busy={verifying}
                    data-testid={dataTestId && `${dataTestId}.verify-btn`}
                >
                    {verifying ? 'Verifying...' : 'Verify and continue'}
                </Button>
            </Box>
            {sendError}
            <Typography component="p" sx={bodySx} data-testid={dataTestId && `${dataTestId}.resend-text`}>
                Didn&apos;t get a code?{' '}
                {countingDown ? (
                    <span data-testid={dataTestId && `${dataTestId}.resend-countdown`}>
                        Resend in <strong data-testid={dataTestId && `${dataTestId}.resend-countdown-time`}>{formatCountdown(secondsLeft)}</strong>
                    </span>
                ) : (
                    <Button
                        variant="text"
                        sx={linkButtonSx}
                        onClick={() => sendCode(SendCodeTriggers.RESEND)}
                        disabled={sending}
                        data-testid={dataTestId && `${dataTestId}.resend-btn`}
                    >
                        Resend
                    </Button>
                )}
            </Typography>
            {helpRow}
        </>
    );

    return (
        <Dialog
            open
            onClose={handleDialogClose}
            aria-labelledby={TITLE_ID}
            aria-describedby={DESCRIPTION_ID}
            PaperProps={{ sx: paperSx, 'data-testid': dataTestId && `${dataTestId}.dialog` }}
            data-testid={dataTestId && `${dataTestId}.overlay`}
        >
            {step === PromptSteps.PRESEND ? renderPresend() : renderEntry()}
        </Dialog>
    );
};

EmailVerificationPromptModal.propTypes = {
    email: PropTypes.string.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onVerified: PropTypes.func.isRequired,
    'data-testid': PropTypes.string,
};

EmailVerificationPromptModal.defaultProps = {
    'data-testid': null,
};

export default EmailVerificationPromptModal;
